import React from 'react';
import { FileText, Download, Target, BarChart3, ShieldCheck, BookOpen } from 'lucide-react';
import { REPORT_PERIOD } from '../mitre-data';

export default function ReportTemplates({ onPreview, filter }) {
  const templates = [
    {
      id:           'mitre',
      name:         'MITRE ATT&CK Security Assessment',
      description:  'Full tactic coverage mapping, top technique analysis and prioritised remediation plan.',
      type:         'Strategic',
      frequency:    'Monthly',
      lastGenerated: 'May 25, 2026',
      icon:         Target,
      iconColor:    '#f97316',
      iconBg:       'rgba(249,115,22,0.15)',
      period:       REPORT_PERIOD,
      canPreview:   true,
    },
    {
      id:           'posture',
      name:         'Security Posture Report',
      description:  'Domain-level scores versus industry benchmark with control gaps and recommendations.',
      type:         'Executive',
      frequency:    'Monthly',
      lastGenerated: 'May 25, 2026',
      icon:         ShieldCheck,
      iconColor:    '#34d399',
      iconBg:       'rgba(52,211,153,0.15)',
      period:       REPORT_PERIOD,
      canPreview:   true,
    },
    {
      id:           'incident',
      name:         'Incident Summary Report',
      description:  'Week-by-week incident breakdown by severity, category, affected assets, MITRE mapping and response metrics.',
      type:         'Operational',
      frequency:    'Weekly',
      lastGenerated: 'May 25, 2026',
      icon:         BarChart3,
      iconColor:    '#60a5fa',
      iconBg:       'rgba(96,165,250,0.15)',
      period:       'May 19 – May 25, 2026',
      canPreview:   true,
    },
    {
      id:           'threat-intelligence',
      name:         'Threat Intelligence Digest',
      description:  'Latest threat indicators, observed attack patterns and external intelligence feeds.',
      type:         'Strategic',
      frequency:    'Daily',
      lastGenerated: 'May 25, 2026',
      icon:         BookOpen,
      iconColor:    '#a78bfa',
      iconBg:       'rgba(167,139,250,0.15)',
      period:       'Last 24 hours',
      canPreview:   false,
    },
    {
      id:           'compliance',
      name:         'Compliance Audit Report',
      description:  'Multi-framework compliance scoring (NIST CSF, ISO 27001, CIS v8, GDPR) with findings and remediation roadmap.',
      type:         'Compliance',
      frequency:    'Monthly',
      lastGenerated: 'May 25, 2026',
      icon:         FileText,
      iconColor:    '#7c3aed',
      iconBg:       'rgba(124,58,237,0.15)',
      period:       'Q2 2026',
      canPreview:   true,
    },
  ];

  // When a filter is active, show only the matching template
  const visibleTemplates = filter
    ? templates.filter((t) => t.id === filter)
    : templates;

  return (
    <div className="report-templates">
      <div className="report-templates__header">
        <h3 className="report-templates__title">Report Templates</h3>
        <p className="report-templates__subtitle">
          {filter
            ? 'Pre-configured report for this view — click Preview to view or download as PDF'
            : 'Pre-configured reports — click Preview to view or generate a PDF'
          }
        </p>
      </div>
      <div className="report-templates__grid">
        {visibleTemplates.map((template) => {
          const Icon = template.icon;
          return (
            <div key={template.id} className={`report-template${template.canPreview ? ' report-template--featured' : ''}`}>
              <div className="report-template__header">
                <div className="report-template__info">
                  <div className="report-template__icon" style={{ background: template.iconBg }}>
                    <Icon size={20} style={{ color: template.iconColor }} />
                  </div>
                  <div className="report-template__details">
                    <h4 className="report-template__name">{template.name}</h4>
                    <p className="report-template__description">{template.description}</p>
                  </div>
                </div>
                <div className="report-template__status">
                  <span className="chip chip--success">Active</span>
                </div>
              </div>

              <div className="report-template__meta">
                <span>Type: {template.type}</span>
                <span>Frequency: {template.frequency}</span>
                <span className="report-template__period">{template.period}</span>
              </div>

              <div className="report-template__footer">
                <span className="report-template__last-generated">
                  Last generated: {template.lastGenerated}
                </span>
                <div className="report-template__actions">
                  <button className="btn btn--outline btn--sm">
                    <Download size={12} /> Download
                  </button>
                  {template.canPreview ? (
                    <button className="btn btn--primary btn--sm" onClick={() => onPreview(template.id)}>
                      <FileText size={12} /> Preview PDF
                    </button>
                  ) : (
                    <button className="btn btn--outline btn--sm">Generate</button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
